import type { NexperienceDatesWithTimeslotsFragment } from '@data-access/lib/gql/experience/documents'
import { baseUrl } from '@utils/utils-config'
import type { ExperienceStructuredOffer } from 'src/lib/structured-data/experience'
import type { ExperienceDayPrices, HeadlinePrice } from './day-prices'
import {
  getExperienceSearchParams,
  getExperienceSelectionLabel,
  mapDatesToEdpTimeslots,
} from './experience-booking-utils'
import { normalizeExperienceInterval } from './experience-date-range'
import type { Experience, ExperienceFilter } from './experience-state-types'

/** Exact appointment offers describe the same five cards and selected party as the UI. */
export function getExperienceStructuredOffers(
  experience: Experience,
  filter: ExperienceFilter,
  dates: NexperienceDatesWithTimeslotsFragment[],
  dayPrices?: ExperienceDayPrices
): ExperienceStructuredOffer[] {
  // Admission cards show category-dependent starting prices, not an exact party offer.
  if (experience.experienceType === 'ticket') return []
  const variant = experience.variants.find(item => item.id === filter.selectedVariant)
  const options =
    variant?.options.filter(option => (filter.selectedOptions?.[option.id] ?? 0) > 0) ?? []
  const selection = getExperienceSelectionLabel(filter, experience.variants)
  if (!options.length || !selection) return []

  return mapDatesToEdpTimeslots(dates, options, filter.selectedOptions, dayPrices)
    .slice(0, 5)
    .filter(slot => Number.isFinite(slot.price) && slot.price >= 0)
    .map(slot => ({
      url: baseUrl(
        `/erlebnisse/${experience.publicId}/${experience.slug}/termine/${getExperienceSearchParams({
          ...filter,
          ...normalizeExperienceInterval({ from: new Date(`${slot.date}T12:00:00Z`) }),
        })}`
      ),
      name: `${slot.date} ${slot.startTime} · ${variant?.title ?? experience.title} · ${selection}`,
      priceInCents: slot.price,
      // A returned timeslot alone does not prove capacity or booking cutoff.
    }))
}

/** Without exact offers, the visible headline price is offered, with its bookability. */
export function getExperiencePriceFromOffers(
  experience: Pick<Experience, 'publicId' | 'slug' | 'hasMultiplePrices'>,
  price: HeadlinePrice,
  availability: 'InStock' | 'MadeToOrder' | 'OutOfStock'
): ExperienceStructuredOffer[] {
  if (!price) return []
  return [
    {
      url: baseUrl(`/erlebnisse/${experience.publicId}/${experience.slug}/`),
      priceInCents: price.price,
      isPriceFrom: experience.hasMultiplePrices,
      availability: `https://schema.org/${availability}`,
    },
  ]
}
